<template>
  <div class="w-[min(30rem,calc(100vw-4rem))]">
    <div class="flex items-center justify-between gap-2 mb-3">
      <div class="flex min-w-0 items-center gap-2">
        <IconStair  width="14" height="14" class="text-[var(--p-primary-color)]" />
        <h3
          class="truncate font-semibold text-sm text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]"
        >
          {{ $t('whatsapp.actions.quick_replies') }}
        </h3>
      </div>

      <Button
        text
        rounded
        size="small"
        severity="secondary"
        class="p-0.5!"
        v-tooltip.top="$t('shortcut.add_shortcut')"
        @click="openCreateModal"
      >
        <template #icon>
          <IconPlus width="14" height="14" class="text-[var(--p-primary-color)]" />
        </template>
      </Button>
    </div>

    <AppInput
      v-model="searchTerm"
      type="search"
      :placeholder="$t('shortcut.search_placeholder')"
      size="small"
      class="mb-3"
    >
      <template #icon>
        <Search :size="14" class="text-[var(--p-primary-color)]" />
      </template>
    </AppInput>

    <div class="h-64 overflow-y-auto">
      <div v-if="store.loading" class="flex h-full items-center justify-center">
        <i class="pi pi-spin pi-spinner text-[var(--p-primary-color)]" />
      </div>

      <div
        v-else-if="store.shortcuts.length === 0"
        class="flex h-full items-center justify-center px-4 text-center text-xs text-[var(--p-surface-400)]"
      >
        {{ $t('shortcut.empty') }}
      </div>

      <div
        v-else-if="filteredShortcuts.length === 0"
        class="flex h-full items-center justify-center px-4 text-center text-xs text-[var(--p-surface-400)]"
      >
        {{ $t('shortcut.no_results') }}
      </div>

      <div v-else class="space-y-0.5">
        <div
          v-for="shortcut in filteredShortcuts"
          :key="shortcut.id"
          class="group w-full flex items-center gap-1 rounded-lg hover:!bg-[var(--p-primary-50)] dark:hover:!bg-[var(--p-primary-950)] transition-colors pr-1"
        >
          <Button
            text
            type="button"
            class="min-w-0 flex-1! flex items-start gap-2.5 text-left px-2 py-2 rounded-lg justify-start! hover:!bg-transparent"
            @click="emit('select-reply', shortcut.description)"
          >
            <span
              v-if="shortcut.combination"
              class="text-xs dark:dark:text-[var(--p-surface-0)] font-mono bg-gray-100 dark:bg-neutral-700 text-[var(--p-primary-color)] rounded px-1.5 py-0.5 flex-shrink-0 mt-0.5"
            >
              {{ shortcut.combination }}
            </span>
            <div class="min-w-0">
              <div
                class="text-sm font-medium text-[var(--p-surface-800)] dark:text-[var(--p-surface-50)] truncate"
              >
                {{ shortcut.name }}
              </div>
              <div class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)] line-clamp-2">
                {{ shortcut.description }}
              </div>
            </div>
          </Button>
          <Button
            text
            rounded
            size="small"
            severity="secondary"
            class="p-1.5! mt-1 lg:opacity-0 group-hover:opacity-500 focus-visible:opacity-100 transition-opacity hover:bg-[var-(--p-primary-200)]"
            :aria-label="$t('shortcut.edit_shortcut')"
            v-tooltip.top="$t('shortcut.edit_shortcut')"
            @click.stop="openEditModal(shortcut)"
          >
            <template #icon>
              <Pencil :size="14" class="text-[var(--p-primary-color)]" />
            </template>
          </Button>
        </div>
      </div>
    </div>

    <AppDialog
      v-model="isCreateModalOpen"
      width="42rem"
      custom-class="quick-replies-shortcut-dialog backdrop-blur-xl"
      :draggable="false"
      :dismissable-mask="false"
      @update:modelValue="handleModalVisibilityChange"
    >
      <template #header>
        <div class="flex items-center gap-2">
          <IconStair width="1.25rem" height="1.25rem" class="text-[var(--p-primary-color)]" />
          <h3
            class="truncate font-semibold text-sm text-[var(--p-surface-700)] dark:text-[var(--p-surface-0)]"
          >
            {{ $t(isEditing ? 'shortcut.edit_title' : 'shortcut.create_title') }}
          </h3>
        </div>
      </template>
      <template #content>
        <form class="space-y-4 pt-2" @click.stop @mousedown.stop @submit.prevent="saveShortcut">
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <AppInput
              v-model="form.name"
              :label="$t('shortcut.form.name')"
              :placeholder="$t('shortcut.form.name_placeholder')"
              :error-message="submitted && !form.name.trim() ? $t('general.required_field') : ''"
            />

            <AppInput
              v-model="form.combination"
              :label="$t('shortcut.form.combination')"
              :placeholder="$t('shortcut.form.combination_placeholder')"
              readonly
              :error-message="
                submitted && !form.combination.trim() ? $t('general.required_field') : ''
              "
            >
              <template #suffix>
                <Info
                  :size="14"
                  class="text-[var(--p-primary-color)]"
                  v-tooltip.top="$t('shortcut.combination_info')"
                />
              </template>
            </AppInput>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <AppSelect
              v-model="form.type"
              :label="$t('shortcut.form.type')"
              :placeholder="$t('shortcut.form.type_placeholder')"
              :options="shortcutTypeOptions"
              :show-clear="false"
              append-to="self"
            />

            <AppMultiselect
              v-if="isPublicShortcut"
              v-model="form.campaignIds"
              :label="$t('shortcut.campaign_ids')"
              :placeholder="$t('shortcut.form.campaigns_placeholder')"
              :options="campaignOptions"
              :loading="loadingCampaigns"
              filter
              append-to="self"
              :error-message="
                submitted && form.campaignIds.length === 0 ? $t('general.required_field') : ''
              "
            />
          </div>

          <div class="space-y-1">
            <AppEditorMessage
              v-model="form.message"
              :placeholder="$t('shortcut.form.message_placeholder')"
              :text-editor="$t('shortcut.form.message')"
              :error-message="submitted && !form.message.trim() ? $t('general.required_field') : ''"
              :show-preview="false"
              :rows="5"
            />
          </div>
        </form>
      </template>

      <template #footer>
        <div class="flex justify-end gap-2" @click.stop @mousedown.stop>
          <AppButton
            class="!w-auto"
            variant="secondary"
            outlined
            :label="$t('general.cancel')"
            :block="false"
            :disabled="saving"
            @click="closeCreateModal"
          />
          <AppButton
            class="!w-auto"
            :label="$t('general.save')"
            icon="pi pi-save"
            :block="false"
            :loading="saving"
            @click="saveShortcut"
          />
        </div>
      </template>
    </AppDialog>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import AppEditorMessage from '@/components/atoms/editor/AppEditorMessage.vue'
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import AppMultiselect from '@/components/atoms/selects/AppMultiselect.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import type { SelectOption } from '@/components/atoms/selects/types/select-option.types'
import Button from 'primevue/button'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import { MessageShortcutStatus, MessageShortcutType } from '@/services/shortcut/enums'
import type {
  ICreateShortcut,
  IUpdateShortcut,
  MessageShortcutDto,
} from '@/services/shortcut/interfaces'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useShortcutService } from '@/services/shortcut/useShortcutService'
import { useAuthStore } from '@/stores/useAuthStore'
import { useShortcutStore } from '@/stores/useShortcutStore'
import IconPlus from '@/assets/svg/plus_icon.svg?component'
import IconStair from '@/assets/svg/stair_icon.svg?component'
import { Info, Pencil, Search } from 'lucide-vue-next'

const emit = defineEmits<{
  'select-reply': [text: string]
}>()

const store = useShortcutStore()
const authStore = useAuthStore()
const { createShortcut, updateShortcut } = useShortcutService()
const { getAllCampaigns } = useCampaignService()
const { t } = useI18n()

const searchTerm = ref('')
const isCreateModalOpen = ref(false)
const editingShortcut = ref<MessageShortcutDto | null>(null)
const saving = ref(false)
const submitted = ref(false)
const loadingCampaigns = ref(false)
const campaignOptions = ref<SelectOption[]>([])

const isEditing = computed(() => Boolean(editingShortcut.value))

const form = reactive({
  name: '',
  combination: '',
  message: '',
  type: MessageShortcutType.PRIVATE,
  campaignIds: [] as string[],
})

const shortcutTypeOptions = computed<SelectOption[]>(() => [
  { name: t('shortcut.type_options.private'), value: MessageShortcutType.PRIVATE },
  { name: t('shortcut.type_options.public'), value: MessageShortcutType.PUBLIC },
])

const isPublicShortcut = computed(() => form.type === MessageShortcutType.PUBLIC)

watch(
  () => form.name,
  (name) => {
    form.combination = name.trim() ? `/${name.trim()}` : ''
  },
)

const filteredShortcuts = computed(() => {
  const query = searchTerm.value.trim().toLowerCase()
  if (!query) return store.shortcuts

  return store.shortcuts.filter((shortcut) => {
    const name = shortcut.name?.toLowerCase() ?? ''
    const combination = shortcut.combination?.toLowerCase() ?? ''

    return name.includes(query) || combination.includes(query)
  })
})

const isFormValid = computed(() =>
  Boolean(
    form.name.trim() &&
      form.combination.trim() &&
      form.message.trim() &&
      authStore.userId &&
      (!isPublicShortcut.value || form.campaignIds.length > 0),
  ),
)

const resetForm = () => {
  form.name = ''
  form.combination = ''
  form.message = ''
  form.type = MessageShortcutType.PRIVATE
  form.campaignIds = []
  submitted.value = false
}

const loadCampaignOptions = async () => {
  if (campaignOptions.value.length || loadingCampaigns.value) return

  try {
    loadingCampaigns.value = true
    const campaigns = await getAllCampaigns({ campaign_type: TypeCampaign.WHATSAPP })
    campaignOptions.value = campaigns.map((campaign) => ({
      name: campaign.name,
      value: campaign.id,
    }))
  } finally {
    loadingCampaigns.value = false
  }
}

const openCreateModal = () => {
  editingShortcut.value = null
  resetForm()
  isCreateModalOpen.value = true
}

const openEditModal = (shortcut: MessageShortcutDto) => {
  editingShortcut.value = shortcut
  form.name = shortcut.name
  form.combination = shortcut.combination ?? ''
  form.message = shortcut.description
  form.type = shortcut.type ?? MessageShortcutType.PRIVATE
  form.campaignIds = shortcut.campaign_ids ? [...shortcut.campaign_ids] : []
  submitted.value = false
  isCreateModalOpen.value = true
}

const closeCreateModal = () => {
  isCreateModalOpen.value = false
  editingShortcut.value = null
  resetForm()
}

const handleModalVisibilityChange = (visible: boolean) => {
  if (!visible && !saving.value) resetForm()
}

const handleShortcutTypeChange = () => {
  if (isPublicShortcut.value) {
    void loadCampaignOptions()
    return
  }

  form.campaignIds = []
}

watch(() => form.type, handleShortcutTypeChange)

const saveShortcut = async () => {
  submitted.value = true
  if (!isFormValid.value || saving.value || !authStore.userId) return

  const isPublic = isPublicShortcut.value
  const payload = {
    name: form.name.trim(),
    combination: form.combination.trim(),
    description: form.message.trim(),
    campaign_ids: isPublic ? form.campaignIds : [],
    user_ids: isPublic ? [] : [authStore.userId],
    status: MessageShortcutStatus.ACTIVE,
    type: form.type,
  }

  try {
    saving.value = true
    if (editingShortcut.value) {
      const updatePayload: IUpdateShortcut = payload
      const updatedShortcut = await updateShortcut(editingShortcut.value.id, updatePayload)
      const shortcutIndex = store.shortcuts.findIndex(({ id }) => id === updatedShortcut.id)
      if (shortcutIndex !== -1) store.shortcuts.splice(shortcutIndex, 1, updatedShortcut)
    } else {
      const createPayload: ICreateShortcut = payload
      const createdShortcut = await createShortcut(createPayload)
      store.shortcuts.unshift(createdShortcut)
    }
    closeCreateModal()
  } finally {
    saving.value = false
  }
}
</script>
